import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { el as elDateFns, enGB as enDateFns } from 'date-fns/locale';
import en from './locales/en.json';
import el from './locales/el.json';

// The single place that knows what languages exist. Before this file held a
// registry, "what languages are there" was spread across seven files: three
// copies of `i18n.language === 'el' ? 'en' : 'el'` in the language toggles, four
// copies of `localStorage.getItem('glamgo-lang') || 'el'`, and a per-page
// `i18n.language === 'el' ? elLocale : undefined` for date-fns. Adding a third
// language meant finding all of them, and missing one produced a page that was
// half-translated rather than an error.
//
// `load` exists so that a language added later is fetched only when chosen. The
// two shipped locales resolve to an already-imported object, which keeps the
// current behaviour exactly as it was — no loading state, no flash of untranslated
// text on first paint. A third language is one entry:
//
//   ru: { ..., load: () => import('./locales/ru.json').then((m) => m.default) }
//
// and nothing else in the app changes.
export const LANGUAGES = {
  el: {
    label: 'Ελληνικά',
    short: 'ΕΛ',
    // What goes in <html lang>. Kept separate from the key because a future
    // locale may need a region ('pt-BR'), and screen readers and Google both
    // read this attribute, not our key.
    htmlLang: 'el',
    // BCP-47 tag for Intl (toLocaleDateString, Intl.NumberFormat). el-CY, not
    // el-GR: same language, but the customer is in Cyprus.
    intl: 'el-CY',
    dateFns: elDateFns,
    load: () => Promise.resolve(el),
  },
  en: {
    label: 'English',
    short: 'EN',
    htmlLang: 'en',
    intl: 'en-GB',
    // enGB, not enUS: this is a Cypriot business. "5 August 2026" and a 24-hour
    // clock, not "August 5, 2026" and AM/PM.
    dateFns: enDateFns,
    load: () => Promise.resolve(en),
  },
};

export const DEFAULT_LANGUAGE = 'el';
export const STORAGE_KEY = 'glamgo-lang';

// localStorage throws rather than returning null in Safari private browsing, and
// a language preference is not worth a white screen.
const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};
const writeStored = (lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* ignore */
  }
};

// The language named by the URL's first path segment, for /el/… and /en/… .
//
// Deliberately duplicated rather than imported from routing.js: that module
// imports this one, and the URL has to be read here, before i18next is
// initialised, so that a visitor landing directly on /en/browse gets an English
// first paint instead of a Greek one that flips a tick later. Whoever reads the
// shared link — and Googlebot — sees the language the URL promised.
function languageFromUrl() {
  if (typeof window === 'undefined') return null;
  const first = window.location.pathname.split('/')[1];
  return first && LANGUAGES[first] ? first : null;
}

// Order: the URL, then an explicit choice the visitor made, then what their
// browser asks for, then Greek.
//
// The URL wins over the stored preference because it is the more specific
// signal: someone who was sent /en/product/x wants that page in English, even
// if they last browsed in Greek.
//
// The browser step is what makes the site usable outside Cyprus. Until now every
// first-time visitor was served Greek regardless of where they were or what
// their device asked for, because the language was read from localStorage with a
// hardcoded `|| 'el'` and nothing ever consulted navigator.
function detect() {
  const fromUrl = languageFromUrl();
  if (fromUrl) return fromUrl;

  const saved = readStored();
  if (saved && LANGUAGES[saved]) return saved;

  const tags = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  for (const tag of tags) {
    if (!tag) continue;
    // 'en-GB' and 'en' both mean the English bundle. Region only matters if we
    // ever ship two variants of one language, which is why the whole tag is
    // checked first.
    const lower = String(tag).toLowerCase();
    if (LANGUAGES[lower]) return lower;
    const base = lower.split('-')[0];
    if (LANGUAGES[base]) return base;
  }
  return DEFAULT_LANGUAGE;
}

const initial = detect();

// Written back immediately, and this is load-bearing rather than tidy:
// utils/api.js reads this key directly to set Accept-Language, deliberately not
// importing i18n so that talking to the API does not pull the locale bundles in.
// If detection left the key empty, a visitor whose browser asked for English
// would read an English page while every server-rendered error came back in
// Greek. Persisting the detected value keeps that reader correct without it
// having to know how detection works.
writeStored(initial);

const applyHtmlLang = (lng) => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = LANGUAGES[lng]?.htmlLang || lng;
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      el: { translation: el },
    },
    lng: initial,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: Object.keys(LANGUAGES),
    interpolation: {
      escapeValue: false,
      // Seven strings tell the reader to write to support@… — a locker with no
      // pickup code, a dispute window that has closed, three reasons an
      // extension cannot be charged. They each carried the address as literal
      // text, on glamgo.cy, a domain with no MX record: every one of those
      // sentences sent a stuck customer's email into a black hole.
      //
      // As a default variable the address is filled in from the backend's own
      // company configuration (see utils/useCompany), so it is right in both
      // locales at once and changing COMPANY_EMAIL changes it everywhere with
      // no rebuild. The seed value is the same fallback useCompany uses, so the
      // strings are correct on the very first paint, before /api/company
      // answers.
      defaultVariables: { supportEmail: 'support@glamgo.hair' },
    },
  });

/**
 * Point the {{supportEmail}} in the locale files at the configured address.
 *
 * Called by useCompany once /api/company answers. The re-render is only
 * triggered when the address actually differs from what is already on screen,
 * which in the normal case (backend agrees with the seed) means this costs
 * nothing.
 */
export function setSupportEmail(email) {
  if (!email || typeof email !== 'string') return;
  const vars = i18n.options.interpolation.defaultVariables;
  if (vars.supportEmail === email) return;
  vars.supportEmail = email;
  // Nothing else re-reads an already-rendered string. Re-selecting the current
  // language is the supported way to tell every useTranslation subscriber that
  // its output has changed.
  i18n.changeLanguage(i18n.language);
}

// index.html ships lang="el" because that is the common case and the attribute
// has to be right before React boots. Everything after that is this.
applyHtmlLang(initial);

/** The language currently being read. Safe to call outside the React tree. */
export const getLanguage = () => (LANGUAGES[i18n.language] ? i18n.language : DEFAULT_LANGUAGE);

/** The date-fns locale for the current language — for `format(date, pattern, { locale })`. */
export const getDateLocale = () => LANGUAGES[getLanguage()].dateFns;

/**
 * BCP-47 tag for the Intl APIs — `toLocaleDateString`, `Intl.NumberFormat`.
 *
 * Passing this rather than nothing is the whole point: `toLocaleDateString()`
 * with no argument formats in the *browser's* locale, not the app's, so a
 * customer reading the Greek site on an English phone got Greek prose with
 * English dates in the middle of it.
 */
export const getIntlLocale = () => LANGUAGES[getLanguage()].intl;

/**
 * Switch language: loads the bundle if it is not in memory, tells i18next,
 * persists the choice, and updates <html lang>. Callers that also want the
 * choice saved against the account call rememberLocale() from utils/locale.
 */
export async function setLanguage(lng) {
  const entry = LANGUAGES[lng];
  if (!entry) return;
  if (!i18n.hasResourceBundle(lng, 'translation')) {
    i18n.addResourceBundle(lng, 'translation', await entry.load(), true, true);
  }
  await i18n.changeLanguage(lng);
  writeStored(lng);
  applyHtmlLang(lng);
}

/**
 * The next language in the registry, for the toggle in the navbar. Cycles, so
 * adding a third language turns the two-state toggle into a three-state one
 * without touching the components.
 */
export function nextLanguage(current = getLanguage()) {
  const keys = Object.keys(LANGUAGES);
  return keys[(keys.indexOf(current) + 1) % keys.length];
}

export default i18n;
